import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Alert, Button, DiscordIcon, FormField, Input } from '@playerbase/ui';
import { SignInEmailFormSchema } from '@playerbase/types';

import { supabase } from '../lib/supabase.js';
import { getSiteUrl } from '../lib/site-url.js';
import { useSharedEmail } from '../lib/use-shared-email.js';
import { stashInviteToken } from '../auth/invite-redemption.js';
import { AuthShell } from './auth-shell.js';

export function SignInPage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Threaded from /invite/:token's "Log in" link so we can route the user
  // back to the invite-accept page once signed in (where the logged-in
  // branch shows the Accept button).
  const inviteToken = searchParams.get('invite');
  const flash = (location.state as { flash?: string } | null)?.flash;
  const [email, setEmail] = useSharedEmail();
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [serverError, setServerError] = useState('');
  const [oauthError, setOauthError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleDiscordSignIn(): Promise<void> {
    setOauthError('');
    if (inviteToken !== null) stashInviteToken(inviteToken);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: {
        scopes: 'identify email',
        redirectTo: `${getSiteUrl()}/auth/callback`,
      },
    });
    if (error) {
      setOauthError(error.message);
    }
  }

  async function handleEmailSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setEmailError('');
    setPasswordError('');
    setServerError('');

    const result = SignInEmailFormSchema.safeParse({ email, password });
    if (!result.success) {
      for (const issue of result.error.issues) {
        if (issue.path[0] === 'email') setEmailError(issue.message);
        if (issue.path[0] === 'password') setPasswordError(issue.message);
      }
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: result.data.email,
        password: result.data.password,
      });
      if (error) {
        setServerError(error.message);
        return;
      }
      // When the user reached us via /invite/:token's "Log in" link, route
      // back there so the logged-in branch's Accept button surfaces. The
      // PublicOnlyRoute redirect would otherwise drop them on their default
      // studio with the invite token still stranded in localStorage.
      if (inviteToken !== null) {
        navigate(`/invite/${encodeURIComponent(inviteToken)}`, { replace: true });
        return;
      }
      // Otherwise the auth context's onAuthStateChange + PublicOnlyRoute
      // handles the redirect.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell title="Log in" subtitle="Welcome back to PlayerBase">
      <div className="flex flex-col gap-6">
        {flash === 'email-verified' && (
          <Alert tone="success">
            Email verified. Please log in below to continue.
          </Alert>
        )}

        {/* Discord OAuth */}
        <div className="flex flex-col gap-3">
          <Button
            variant="discord"
            className="w-full"
            onClick={() => { void handleDiscordSignIn(); }}
          >
            <DiscordIcon className="h-4 w-auto" />
            Sign in with Discord
          </Button>
          {oauthError !== '' && (
            <Alert tone="danger">{oauthError}</Alert>
          )}
        </div>

        {/* Divider */}
        <div className="relative flex items-center gap-3">
          <div className="flex-1 border-t border-border" />
          <span className="text-xs text-text-muted">or</span>
          <div className="flex-1 border-t border-border" />
        </div>

        {/* Email form */}
        <form onSubmit={(e) => { void handleEmailSubmit(e); }} className="flex flex-col gap-4" noValidate>
          {serverError !== '' && (
            <Alert tone="danger">{serverError}</Alert>
          )}

          <FormField error={emailError !== '' ? emailError : undefined}>
            <FormField.Label>Email</FormField.Label>
            <FormField.Control>
              <Input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
              />
            </FormField.Control>
            {emailError !== '' && <FormField.ErrorText>{emailError}</FormField.ErrorText>}
          </FormField>

          <FormField error={passwordError !== '' ? passwordError : undefined}>
            <FormField.Label>Password</FormField.Label>
            <FormField.Control>
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full"
              />
            </FormField.Control>
            {passwordError !== '' && <FormField.ErrorText>{passwordError}</FormField.ErrorText>}
          </FormField>

          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="text-sm text-text-muted underline underline-offset-2 hover:text-text transition-colors"
            >
              Forgot password?
            </Link>
          </div>

          <Button type="submit" variant="primary" className="w-full" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Log in'}
          </Button>
        </form>

        {/* Footer */}
        <p className="text-center text-sm text-text-muted">
          Need an account?{' '}
          <Link to="/sign-up" className="text-accent underline underline-offset-2 hover:opacity-80 transition-opacity">
            Sign up
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
